import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  inspectionsApi,
  Inspection,
  Room,
  Finding,
  RoomComponent,
  ComponentAttribute,
  ComponentComment,
} from '@/api/inspections';
import { agreementsApi } from '@/api';
import type { Agreement } from '@/types';
import ConfirmDialog from '@/components/ConfirmDialog';
import ComponentList from '@/components/ComponentList';
import VoiceRecordButton, { type VoiceRecordHandle } from '@/components/VoiceRecordButton';
import { SaveToLibrarySheet } from '@/components/library/SaveToLibrarySheet';
import { AgreementSendPanel } from '@/components/AgreementSendPanel';
import {
  Badge,
  Button,
  PageHeader,
  ProgressBar,
  QuickActionBar,
  Tabs,
  cn,
  type QuickAction,
  type TabItem,
} from '@/ui';
import { STATUS_META, pendingComponentCount } from './InspectionView/_constants';
import { LoadingState, ErrorState } from './InspectionView/states';
import { CompletedReportCta } from './InspectionView/CompletedReportCta';
import { AddRoomForm } from './InspectionView/AddRoomForm';
const InspectionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [activeComponentId, setActiveComponentId] = useState<string | null>(null);
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [completing, setCompleting] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteRoomId, setPendingDeleteRoomId] = useState<string | null>(null);
  /**
   * Map of componentId → trigger function. Each ComponentRow registers its
   * hidden file-input trigger on mount. The Photo FAB calls the active
   * component's trigger instead of handling upload itself — all the upload,
   * analysis, loading state, and local-photo management stay in ComponentRow
   * where they belong.
   */
  const componentPhotoTriggersRef = useRef<Map<string, () => void>>(new Map());
  const [uploadingCoverPhoto, setUploadingCoverPhoto] = useState(false);
  const [deletingCoverPhoto, setDeletingCoverPhoto] = useState(false);
  const coverPhotoInputRef = useRef<HTMLInputElement>(null);
  const [savingToLibrary, setSavingToLibrary] = useState<{
    roomName: string;
    findingId: string;
    /**
     * `componentName` is set when the save was triggered from a component-
     * scoped finding (the AI vision flow per issue #44). Room-level findings
     * pass null and the sheet leaves the field blank.
     */
    componentName: string | null;
    /**
     * Issue #44 — `user_curated` for AI-originated findings the inspector
     * just accepted; defaults to `user_library` for the room-level path.
     */
    source: 'user_library' | 'user_curated';
  } | null>(null);
  /**
   * Bumped after every successful library save so per-component
   * SuggestionsStrip instances refetch and surface the freshly-saved entry.
   * Without this signal the strip caches stale suggestions until the user
   * navigates away. (#44)
   */
  const [librarySaveVersion, setLibrarySaveVersion] = useState(0);
  const voiceRef = useRef<VoiceRecordHandle>(null);

  /**
   * Agreements scoped to this inspection's appointment (#79). Fetched lazily
   * once the inspection loads and we know it has an appointmentId. The
   * latest row drives the status badge + Send/Resend CTA at the top of the
   * page so the inspector never has to detour through Scheduling.
   */
  const [agreements, setAgreements] = useState<Agreement[] | null>(null);

  const loadInspection = useCallback(async () => {
    if (!id) return;
    try {
      const result = await inspectionsApi.get(id);
      if (result.success && result.data) {
        setInspection(result.data);
        setActiveRoomId((prev) => {
          if (prev && result.data!.rooms.find((r) => r.id === prev)) return prev;
          return result.data!.rooms[0]?.id ?? null;
        });
      } else {
        setError('Inspection not found.');
      }
    } catch {
      setError('Failed to load inspection.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInspection();
  }, [loadInspection]);

  useEffect(() => {
    const appointmentId = inspection?.appointmentId;
    if (!appointmentId) {
      setAgreements(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await agreementsApi.listForAppointment(appointmentId);
        if (!cancelled && resp.success && resp.data) setAgreements(resp.data);
      } catch (err) {
        console.error('Failed to load agreements for inspection', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inspection?.appointmentId]);

  // Most-recent agreement for this appointment — mirrors the report-side
  // pattern in ReportBuilder. Drives the badge + CTA at the page top.
  const latestAgreement = useMemo(() => {
    if (!agreements || agreements.length === 0) return null;
    return [...agreements].sort((a, b) => {
      const ta = new Date(a.sentAt ?? a.createdAt ?? 0).getTime();
      const tb = new Date(b.sentAt ?? b.createdAt ?? 0).getTime();
      return tb - ta;
    })[0];
  }, [agreements]);

  const handleAgreementSent = useCallback((agreement: Agreement) => {
    setAgreements((prev) => (prev ? [agreement, ...prev] : [agreement]));
  }, []);

  const activeRoom: Room | null =
    inspection?.rooms.find((r) => r.id === activeRoomId) ?? null;

  useEffect(() => {
    if (!activeRoom) {
      setActiveComponentId(null);
      return;
    }
    if (activeComponentId && !activeRoom.components?.find((c) => c.id === activeComponentId)) {
      setActiveComponentId(null);
    }
  }, [activeRoom, activeComponentId]);

  const handleAddRoom = async (name: string) => {
    if (!id || !name.trim()) return;
    const orderIndex = inspection?.rooms.length ?? 0;
    const result = await inspectionsApi.addRoom(id, { name: name.trim(), order_index: orderIndex });
    if (result.success && result.data) {
      await loadInspection();
      setActiveRoomId(result.data.id);
      setNewRoomName('');
      setAddingRoom(false);
    }
  };

  const handleDeleteRoom = (roomId: string) => {
    setPendingDeleteRoomId(roomId);
  };

  const confirmDeleteRoom = async () => {
    if (!id || !pendingDeleteRoomId) return;
    const roomId = pendingDeleteRoomId;
    setPendingDeleteRoomId(null);
    await inspectionsApi.deleteRoom(id, roomId);
    await loadInspection();
    if (activeRoomId === roomId) {
      setActiveRoomId(inspection?.rooms.find((r) => r.id !== roomId)?.id ?? null);
    }
  };

  const handleFindingUpdated = (updated: Finding) => {
    if (!inspection) return;
    setInspection({
      ...inspection,
      rooms: inspection.rooms.map((room) => ({
        ...room,
        findings: room.findings.map((f) =>
          f.id === updated.id ? { ...f, ...updated, photos: updated.photos ?? f.photos } : f
        ),
      })),
    });
  };

  const handleFindingDeleted = (findingId: string) => {
    if (!inspection) return;
    setInspection({
      ...inspection,
      rooms: inspection.rooms.map((room) => ({
        ...room,
        findings: room.findings.filter((f) => f.id !== findingId),
      })),
    });
  };


  const handleCoverPhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!id) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploadingCoverPhoto(true);
    try {
      const coverPhotoUrl = await inspectionsApi.uploadCoverPhoto(id, file);
      setInspection((prev) => prev ? { ...prev, coverPhotoUrl } : prev);
    } catch (err) {
      console.error('Cover photo upload failed:', err);
    } finally {
      setUploadingCoverPhoto(false);
    }
  };

  const handleDeleteCoverPhoto = async () => {
    if (!id) return;
    setDeletingCoverPhoto(true);
    try {
      await inspectionsApi.deleteCoverPhoto(id);
      setInspection((prev) => prev ? { ...prev, coverPhotoUrl: undefined } : prev);
    } finally {
      setDeletingCoverPhoto(false);
    }
  };

  const handleComponentChanged = useCallback((updated: RoomComponent) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) =>
              room.id === updated.roomId
                ? {
                    ...room,
                    components: (room.components ?? []).map((c) =>
                      c.id === updated.id ? { ...c, ...updated, attributes: c.attributes } : c,
                    ),
                  }
                : room,
            ),
          }
        : prev,
    );
  }, []);

  const handleAttributeChanged = useCallback((updated: ComponentAttribute) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) => ({
              ...room,
              components: (room.components ?? []).map((c) =>
                c.id === updated.componentId
                  ? {
                      ...c,
                      attributes: c.attributes.map((a) =>
                        a.id === updated.id ? { ...a, ...updated } : a,
                      ),
                    }
                  : c,
              ),
            })),
          }
        : prev,
    );
  }, []);

  const handleAttributeDeleted = useCallback((componentId: string, attributeId: string) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) => ({
              ...room,
              components: (room.components ?? []).map((c) =>
                c.id === componentId
                  ? { ...c, attributes: c.attributes.filter((a) => a.id !== attributeId) }
                  : c,
              ),
            })),
          }
        : prev,
    );
  }, []);

  const handleAttributeAdded = useCallback((componentId: string, added: ComponentAttribute) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) => ({
              ...room,
              components: (room.components ?? []).map((c) =>
                c.id === componentId
                  ? { ...c, attributes: [...c.attributes, added] }
                  : c,
              ),
            })),
          }
        : prev,
    );
  }, []);

  const handleComponentAdded = useCallback((added: RoomComponent) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) =>
              room.id === added.roomId
                ? {
                    ...room,
                    components: [
                      ...(room.components ?? []),
                      {
                        ...added,
                        attributes: added.attributes ?? [],
                        comments: added.comments ?? [],
                      },
                    ],
                  }
                : room,
            ),
          }
        : prev,
    );
    setActiveComponentId(added.id);
  }, []);

  // Comment lifecycle — ComponentList mutates server-side and bubbles the
  // result up so the local inspection tree stays in sync without a reload.
  const handleCommentAdded = useCallback(
    (componentId: string, comment: ComponentComment) => {
      setInspection((prev) =>
        prev
          ? {
              ...prev,
              rooms: prev.rooms.map((room) => ({
                ...room,
                components: (room.components ?? []).map((c) =>
                  c.id === componentId
                    ? { ...c, comments: [...(c.comments ?? []), comment] }
                    : c,
                ),
              })),
            }
          : prev,
      );
    },
    [],
  );

  const handleCommentChanged = useCallback(
    (componentId: string, updated: ComponentComment) => {
      setInspection((prev) =>
        prev
          ? {
              ...prev,
              rooms: prev.rooms.map((room) => ({
                ...room,
                components: (room.components ?? []).map((c) =>
                  c.id === componentId
                    ? {
                        ...c,
                        comments: (c.comments ?? []).map((cm) =>
                          cm.id === updated.id ? { ...cm, ...updated } : cm,
                        ),
                      }
                    : c,
                ),
              })),
            }
          : prev,
      );
    },
    [],
  );

  const handleCommentDeleted = useCallback(
    (componentId: string, commentId: string) => {
      setInspection((prev) =>
        prev
          ? {
              ...prev,
              rooms: prev.rooms.map((room) => ({
                ...room,
                components: (room.components ?? []).map((c) =>
                  c.id === componentId
                    ? {
                        ...c,
                        comments: (c.comments ?? []).filter((cm) => cm.id !== commentId),
                      }
                    : c,
                ),
              })),
            }
          : prev,
      );
    },
    [],
  );

  const handleFindingAdded = useCallback((finding: Finding) => {
    setInspection((prev) =>
      prev
        ? {
            ...prev,
            rooms: prev.rooms.map((room) =>
              room.id === finding.roomId
                ? { ...room, findings: [...room.findings, { ...finding, photos: finding.photos ?? [] }] }
                : room,
            ),
          }
        : prev,
    );
  }, []);

  const handleVoiceComplete = async () => {
    await loadInspection();
  };

  const handleComplete = async () => {
    if (!id) return;
    setCompleting(true);
    try {
      const result = await inspectionsApi.updateStatus(id, 'completed');
      if (result.success) {
        setInspection((prev) => prev ? { ...prev, status: 'completed' } : prev);
        setJustCompleted(true);
      }
    } finally {
      setCompleting(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!id) return;
    setGeneratingReport(true);
    setGenerateError(null);
    try {
      const result = await inspectionsApi.generateReport(id);
      if (result.success && result.data?.reportId) {
        navigate(`/reports/${result.data.reportId}`);
      } else {
        setGenerateError('Failed to generate report. Please try again.');
      }
    } catch {
      setGenerateError('Failed to generate report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error || !inspection) {
    return <ErrorState message={error} onBack={() => navigate('/inspections')} />;
  }

  const totalFindings = inspection.rooms.reduce((sum, r) => sum + r.findings.length, 0);
  const includedFindings = inspection.rooms.reduce(
    (sum, r) => sum + r.findings.filter((f) => f.isIncluded).length,
    0
  );

  // Overall "completion" for the progress bar: fraction of components that have
  // either a status set or at least one finding. Falls back to findings progress
  // for legacy inspections with no components.
  const componentsTotal = inspection.rooms.reduce((s, r) => s + (r.components?.length ?? 0), 0);
  const componentsDone = inspection.rooms.reduce((s, r) => {
    const comps = r.components ?? [];
    const findingsByComp = new Set(r.findings.map((f) => f.componentId).filter(Boolean));
    return s + comps.filter((c) => c.inspectionStatus !== null || findingsByComp.has(c.id)).length;
  }, 0);
  const progressMax = componentsTotal > 0 ? componentsTotal : Math.max(totalFindings, 1);
  const progressValue = componentsTotal > 0 ? componentsDone : includedFindings;

  const statusMeta = STATUS_META[inspection.status];

  const tabItems: TabItem[] = inspection.rooms.map((room) => {
    const pending = pendingComponentCount(room);
    return {
      id: room.id,
      label: room.name,
      count: pending,
      countTone: pending > 0 ? 'danger' : 'neutral',
    };
  });

  const activeComponent =
    activeRoom && activeComponentId
      ? (activeRoom.components ?? []).find((c) => c.id === activeComponentId)
      : undefined;

  // Photo is primary per the inspector-mobile kit (im-qbtn--primary) — one-thumb
  // capture puts the most common action front-and-center. Voice is a single tap
  // too, just less frequent.
  const quickActions: QuickAction[] = [
    {
      id: 'photo',
      label: activeComponentId ? 'Component Photo' : 'Photo',
      primary: true,
      icon: (
        <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      // Post-#59: photo always targets a component. Prefer the active
      // component; otherwise fall back to the active room's General
      // (every section snapshots with one).
      onClick: () => {
        const targetId = activeComponentId ?? activeRoom?.components.find(
          (c) => c.name.trim().toLowerCase() === 'general',
        )?.id;
        if (targetId) componentPhotoTriggersRef.current.get(targetId)?.();
      },
    },
    {
      id: 'voice',
      label: 'Voice',
      icon: (
        <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
        </svg>
      ),
      onClick: () => voiceRef.current?.startRecording(),
    },
  ];

  const captureActive = Boolean(activeRoom) && inspection.status === 'in_progress';
  const quickActionContext = activeRoom
    ? activeComponent
      ? `${activeRoom.name} › ${activeComponent.name}`
      : activeRoom.name
    : undefined;

  return (
    <div
      className={cn(
        'max-w-2xl mx-auto',
        // Reserve space for the floating QuickActionBar (~64px + safe area).
        captureActive ? 'pb-28' : 'pb-4',
      )}
    >
      <PageHeader
        eyebrow={
          <button
            type="button"
            onClick={() => navigate('/inspections')}
            className="inline-flex items-center gap-1 hover:text-ink-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Inspections
          </button>
        }
        title={inspection.propertyAddress}
        subtitle={inspection.clientName ?? undefined}
        actions={
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              <Badge tone={statusMeta.tone} dot>{statusMeta.label}</Badge>
              {inspection.status === 'in_progress' && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={completing}
                  disabled={totalFindings === 0}
                  onClick={handleComplete}
                  title={totalFindings === 0 ? 'Add at least one finding first' : 'Mark this inspection complete'}
                >
                  Complete
                </Button>
              )}
            </div>
            {totalFindings > 0 ? (
              <span className="text-caption text-ink-faint tabular-nums">
                {includedFindings}/{totalFindings} findings
              </span>
            ) : (
              <span className="text-caption text-ink-faint">No findings yet</span>
            )}
          </div>
        }
        below={
          <div className="space-y-3">
            <ProgressBar
              value={progressValue}
              max={progressMax}
              tone={progressValue >= progressMax ? 'success' : 'brand'}
              showCount={componentsTotal > 0}
              label={componentsTotal > 0 ? 'Components inspected' : undefined}
            />
            <div className="flex items-center gap-3">
              {inspection.coverPhotoUrl && (
                <div className="relative group w-20 h-14 shrink-0">
                  <img
                    src={inspection.coverPhotoUrl}
                    alt="Property cover photo"
                    className="w-20 h-14 object-cover rounded-lg border border-border-subtle"
                  />
                  <button
                    type="button"
                    onClick={handleDeleteCoverPhoto}
                    disabled={deletingCoverPhoto}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-sev-critical text-white rounded-full items-center justify-center hidden group-hover:flex hover:bg-sev-critical-ink disabled:opacity-50"
                    title="Remove property photo"
                    aria-label="Remove property photo"
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => coverPhotoInputRef.current?.click()}
                disabled={uploadingCoverPhoto}
                className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink-muted transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded"
              >
                {uploadingCoverPhoto ? (
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                {inspection.coverPhotoUrl ? 'Change property photo' : 'Add property photo'}
              </button>
              <input
                ref={coverPhotoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                onChange={handleCoverPhotoSelect}
              />
            </div>
          </div>
        }
      />

      <div className="px-4 md:px-6 py-4 space-y-4">
        {inspection.status === 'completed' && (
          <CompletedReportCta
            justCompleted={justCompleted}
            generatingReport={generatingReport}
            generateError={generateError}
            totalFindings={totalFindings}
            onGenerate={handleGenerateReport}
          />
        )}

        {/* Pre-inspection agreement — surfaced here (#79) so the inspector
            can see status and (re)send without detouring through Scheduling.
            The panel renders its own badge + CTA; once signed, the CTA
            disappears and only the Signed badge (with a "View PDF" link
            when available) remains. */}
        {inspection.appointmentId && (
          <AgreementSendPanel
            appointmentId={inspection.appointmentId}
            clientEmail={inspection.clientEmail}
            agreement={
              latestAgreement
                ? {
                    id: latestAgreement.id,
                    status: latestAgreement.status,
                    sentAt: latestAgreement.sentAt,
                    signedAt: latestAgreement.signedAt,
                    signedPdfUrl: latestAgreement.signedPdfUrl,
                  }
                : null
            }
            onSent={handleAgreementSent}
            variant="card"
          />
        )}

        {/* Section tabs */}
        <div>
          <div className="flex items-center gap-2 flex-wrap pb-1">
            {tabItems.length > 0 && (
              <Tabs
                items={tabItems}
                activeId={activeRoomId ?? ''}
                onChange={(rid) => setActiveRoomId(rid)}
                variant="pills"
                collapsedCount={6}
                label="Inspection sections"
              />
            )}
            <button
              type="button"
              onClick={() => setAddingRoom(true)}
              className={cn(
                'shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full text-body-sm font-medium',
                'border border-dashed border-border text-ink-subtle bg-transparent',
                'hover:border-brand/60 hover:text-brand transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
              )}
            >
              + Section
            </button>
          </div>

          {addingRoom && (
            <AddRoomForm
              existingRoomNames={inspection.rooms.map((r) => r.name)}
              newRoomName={newRoomName}
              setNewRoomName={setNewRoomName}
              onAdd={handleAddRoom}
              onCancel={() => {
                setAddingRoom(false);
                setNewRoomName('');
              }}
            />
          )}
        </div>

        {/* Empty state */}
        {!activeRoom && inspection.rooms.length === 0 && (
          <div className="text-center py-10 border border-dashed border-border rounded-xl bg-surface-1">
            <p className="text-body-sm text-ink-subtle">No sections yet. Add one to start capturing findings.</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => setAddingRoom(true)}
            >
              + Add First Section
            </Button>
          </div>
        )}

        {activeRoom && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-heading-md text-ink">{activeRoom.name}</h2>
              <button
                type="button"
                onClick={() => handleDeleteRoom(activeRoom.id)}
                className="text-caption text-ink-subtle hover:text-sev-critical transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sev-critical rounded"
              >
                Remove section
              </button>
            </div>

            <ComponentList
              inspectionId={inspection.id}
              roomId={activeRoom.id}
              roomName={activeRoom.name}
              components={activeRoom.components ?? []}
              findings={activeRoom.findings}
              activeComponentId={activeComponentId}
              readOnly={inspection.status !== 'in_progress'}
              onComponentFocus={setActiveComponentId}
              onComponentChanged={handleComponentChanged}
              onComponentAdded={handleComponentAdded}
              onAttributeChanged={handleAttributeChanged}
              onAttributeAdded={handleAttributeAdded}
              onAttributeDeleted={handleAttributeDeleted}
              onFindingAdded={handleFindingAdded}
              onFindingUpdated={handleFindingUpdated}
              onFindingDeleted={handleFindingDeleted}
              onCommentAdded={handleCommentAdded}
              onCommentChanged={handleCommentChanged}
              onCommentDeleted={handleCommentDeleted}
              onComponentPhotoTriggerReady={(componentId, trigger) => {
                componentPhotoTriggersRef.current.set(componentId, trigger);
              }}
              onSaveToLibrary={(finding, sectionName, componentName) =>
                setSavingToLibrary({
                  roomName: sectionName,
                  findingId: finding.id,
                  componentName,
                  source: 'user_curated',
                })
              }
              librarySaveVersion={librarySaveVersion}
            />


            {/* Bottom-of-section navigation. Mirrors the section pills at
                the top so the inspector can move section-by-section in
                either direction without scrolling back to the top. */}
            {(() => {
              const idx = inspection.rooms.findIndex((r) => r.id === activeRoom.id);
              const next = idx >= 0 ? inspection.rooms[idx + 1] : null;
              if (!next) return null;
              return (
                <button
                  type="button"
                  onClick={() => {
                    setActiveRoomId(next.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={cn(
                    'w-full inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl',
                    'border border-border-subtle bg-surface-1 text-ink',
                    'hover:bg-surface-2 hover:border-brand/40 transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                  )}
                >
                  <span className="text-body-sm text-ink-subtle">Next section</span>
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    {next.name}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
                    </svg>
                  </span>
                </button>
              );
            })()}
          </div>
        )}

        {/* Both the in_progress "Complete" CTA and the post-complete
            "Generate Report" CTA live near the header now — see the
            top of this body block — so nothing useful sits below the
            section list. The sticky QuickActionBar stays the only
            bottom-anchored chrome. */}
      </div>

      {captureActive && (
        <QuickActionBar actions={quickActions} context={quickActionContext} />
      )}

      {captureActive && activeRoom && (
        <VoiceRecordButton
          ref={voiceRef}
          hideFab
          roomId={activeRoom.id}
          componentId={activeComponent?.id}
          inspectionId={inspection.id}
          context={{
            roomOrArea: activeRoom.name,
            componentName: activeComponent?.name,
            propertyAddress: inspection.propertyAddress,
          }}
          onAnalysisComplete={handleVoiceComplete}
        />
      )}

      {(() => {
        const room = inspection.rooms.find((r) => r.id === pendingDeleteRoomId);
        const count = room?.findings.length ?? 0;
        return (
          <ConfirmDialog
            isOpen={pendingDeleteRoomId !== null}
            title={`Remove "${room?.name ?? 'section'}"?`}
            message={
              count > 0
                ? `This will permanently delete the section and its ${count} finding${count !== 1 ? 's' : ''}. This cannot be undone.`
                : 'This will permanently delete the section. This cannot be undone.'
            }
            confirmLabel="Remove"
            destructive
            onConfirm={confirmDeleteRoom}
            onCancel={() => setPendingDeleteRoomId(null)}
          />
        );
      })()}

      {(() => {
        if (!savingToLibrary || !inspection) return null;
        const room = inspection.rooms.find((r) => r.name === savingToLibrary.roomName);
        const finding = room?.findings.find((f) => f.id === savingToLibrary.findingId);
        if (!finding) return null;
        return (
          <SaveToLibrarySheet
            open
            seed={{
              title: finding.title,
              description: finding.description,
              severity: finding.severity,
              sectionName: savingToLibrary.roomName,
              componentName: savingToLibrary.componentName,
            }}
            source={savingToLibrary.source}
            onClose={() => setSavingToLibrary(null)}
            onSaved={() => {
              setSavingToLibrary(null);
              setLibrarySaveVersion((v) => v + 1);
            }}
          />
        );
      })()}
    </div>
  );
};

export default InspectionView;
